import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { getPersonnel as getPersonnelSql } from '../../../services/migration/personnelDataService';
import { useAuth } from '../../../contexts/AuthContext';
import {
  wmsDefaultPersonnelPermissions,
  WmsPersonnelPermissions,
  wmsPersonnelPermissionService,
} from '../services/wmsPersonnelPermissionService';

interface PersonnelOption {
  id: string;
  name: string;
  email?: string;
}

interface WmsScreenPersonnelModalProps {
  open: boolean;
  screenId: string;
  screenName: string;
  onClose: () => void;
  onChanged: () => void;
}

const permissionLabels: Array<{ key: keyof WmsPersonnelPermissions; label: string }> = [
  { key: 'canShortPick', label: 'Eksik Bildir (Short Pick)' },
  { key: 'canCancelWave', label: 'İş Emri İptal' },
  { key: 'canReassignWave', label: 'İş Emri Yeniden Atama' },
  { key: 'canSelfAssignOrder', label: 'Siparisi Kendine Atayabilsin' },
  { key: 'canCompleteWave', label: 'İş Emrini Tamamla' },
  { key: 'canSorting', label: 'Sortlama' },
];

const WmsScreenPersonnelModal: React.FC<WmsScreenPersonnelModalProps> = ({
  open,
  screenId,
  screenName,
  onClose,
  onChanged,
}) => {
  const { userDetails } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [allPersonnel, setAllPersonnel] = useState<PersonnelOption[]>([]);
  const [selectedPersonnelIds, setSelectedPersonnelIds] = useState<string[]>([]);
  const [selectedPersonnelForPermissions, setSelectedPersonnelForPermissions] = useState<string | null>(null);
  const [personnelPermissionsMap, setPersonnelPermissionsMap] = useState<Record<string, WmsPersonnelPermissions>>({});

  const selectedPersonnel = useMemo(
    () => allPersonnel.filter((p) => selectedPersonnelIds.includes(p.id)),
    [allPersonnel, selectedPersonnelIds]
  );

  useEffect(() => {
    if (!open) return;

    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        const companyId = userDetails?.companyId;
        if (!companyId) {
          setError('Sirket bilgisi bulunamadi.');
          return;
        }

        // Faz 3: personel listesi SQL'den; ekran erisimi + aksiyon izinleri SQL'den (servis HybridFallback yonetir).
        const sqlPersonnel = await getPersonnelSql(companyId);
        const personnelList = await Promise.all(
          (sqlPersonnel || []).map(async (raw: any) => {
            const [screenAccess, action] = await Promise.all([
              wmsPersonnelPermissionService.hasScreenAccess(companyId, raw.id, screenId).catch(() => false),
              wmsPersonnelPermissionService.getScreenActionPermissions(companyId, screenId, raw.id).catch(() => ({ ...wmsDefaultPersonnelPermissions })),
            ]);
            return {
              id: raw.id,
              name: raw?.name || 'Isimsiz Personel',
              email: raw?.email || '',
              screenAccess,
              action,
            };
          })
        );

        const selectedIds = personnelList
          .filter((p: any) => p.screenAccess)
          .map((p: any) => p.id);

        const initialPermissionsMap: Record<string, WmsPersonnelPermissions> = {};
        personnelList.forEach((p: any) => {
          initialPermissionsMap[p.id] = p.action;
        });

        setAllPersonnel(personnelList.map((p: any) => ({ id: p.id, name: p.name, email: p.email })));
        setSelectedPersonnelIds(selectedIds);
        setSelectedPersonnelForPermissions(selectedIds[0] || null);
        setPersonnelPermissionsMap(initialPermissionsMap);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Personel bilgileri yuklenemedi.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [open, screenId, userDetails?.companyId]);

  const handleSelectionChange = (newSelectedIds: string[]) => {
    setSelectedPersonnelIds(newSelectedIds);
    setPersonnelPermissionsMap((prev) => {
      const next = { ...prev };
      newSelectedIds.forEach((personnelId) => {
        if (!next[personnelId]) {
          next[personnelId] = { ...wmsDefaultPersonnelPermissions };
        }
      });
      return next;
    });

    if (!newSelectedIds.includes(selectedPersonnelForPermissions || '')) {
      setSelectedPersonnelForPermissions(newSelectedIds[0] || null);
    }
  };

  const handlePermissionChange = (
    personnelId: string,
    permission: keyof WmsPersonnelPermissions,
    value: boolean
  ) => {
    setPersonnelPermissionsMap((prev) => ({
      ...prev,
      [personnelId]: {
        ...(prev[personnelId] || wmsDefaultPersonnelPermissions),
        [permission]: value,
      },
    }));
  };

  const handleSave = async () => {
    const companyId = userDetails?.companyId;
    if (!companyId) {
      setError('Sirket bilgisi bulunamadi.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      // Screen access flags for all personnel in this company.
      await Promise.all(
        allPersonnel.map((personnel) =>
          wmsPersonnelPermissionService.setScreenAccess(
            companyId,
            personnel.id,
            screenId,
            selectedPersonnelIds.includes(personnel.id)
          )
        )
      );

      // Action permissions only for selected personnel.
      await Promise.all(
        selectedPersonnelIds.map((personnelId) =>
          wmsPersonnelPermissionService.setScreenActionPermissions(
            companyId,
            screenId,
            personnelId,
            personnelPermissionsMap[personnelId] || wmsDefaultPersonnelPermissions
          )
        )
      );

      onChanged();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Kaydetme islemi basarisiz.');
    } finally {
      setSaving(false);
    }
  };

  const handleDialogClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>Personel Yonetimi - {screenName}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Bu ekrani kullanabilecek personelleri secin, sonra sag taraftan aksiyon yetkilerini ayarlayin.
            </Typography>
            <Divider />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Personel Secimi
                </Typography>
                <Autocomplete
                  multiple
                  options={allPersonnel}
                  getOptionLabel={(option) => option.name}
                  value={selectedPersonnel}
                  onChange={(_event, newValue) => handleSelectionChange(newValue.map((p) => p.id))}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Personel Ara/Sec" placeholder="Personel secin" />
                  )}
                  renderTags={() => null}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />

                <Paper variant="outlined" sx={{ mt: 1.5, maxHeight: 300, overflowY: 'auto' }}>
                  <List dense disablePadding>
                    {selectedPersonnel.length === 0 ? (
                      <ListItem>
                        <ListItemText primary="Henuz personel secilmedi." />
                      </ListItem>
                    ) : (
                      selectedPersonnel.map((personnel) => (
                        <ListItem
                          key={personnel.id}
                          selected={selectedPersonnelForPermissions === personnel.id}
                          onClick={() => setSelectedPersonnelForPermissions(personnel.id)}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectionChange(selectedPersonnelIds.filter((id) => id !== personnel.id));
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                          divider
                          sx={{ cursor: 'pointer' }}
                        >
                          <ListItemText
                            primary={personnel.name}
                            secondary={personnel.email || undefined}
                          />
                        </ListItem>
                      ))
                    )}
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Yetki Ayarlari
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, minHeight: 300 }}>
                  {selectedPersonnelForPermissions ? (
                    <>
                      <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600, mb: 1.5 }}>
                        {allPersonnel.find((p) => p.id === selectedPersonnelForPermissions)?.name || 'Secili personel'}
                      </Typography>
                      {permissionLabels.map((perm) => (
                        <FormControlLabel
                          key={perm.key}
                          control={
                            <Switch
                              size="small"
                              checked={
                                personnelPermissionsMap[selectedPersonnelForPermissions]?.[perm.key] ??
                                wmsDefaultPersonnelPermissions[perm.key]
                              }
                              onChange={(e) =>
                                handlePermissionChange(selectedPersonnelForPermissions, perm.key, e.target.checked)
                              }
                            />
                          }
                          label={perm.label}
                        />
                      ))}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Yetki ayarlamak icin soldan bir personel secin.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Iptal</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
          {saving ? <CircularProgress size={18} /> : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WmsScreenPersonnelModal;

